/**
 * 이메일 도메인 관련 자료와 판정.
 *
 * 서버 쪽에도 같은 목록이 public/_includes/util.php 의 getEmailDomains() 에 있다.
 * (PHP 와 JS 라 파일을 공유할 수 없다) 도메인을 늘리거나 줄일 때는 양쪽을 같이 고칠 것.
 */

/** 셀렉트에 넣을 도메인. 국내에서 많이 쓰는 순서대로 */
export const EMAIL_DOMAINS = [
	'naver.com',
	'gmail.com',
	'daum.net',
	'hanmail.net',
	'nate.com',
];

/** 셀렉트 맨 아래 '직접입력' 항목의 값 */
export const EMAIL_DOMAIN_OTHER = 'other';

/** 'user@naver.com' → { local: 'user', domain: 'naver.com' } */
export function splitEmail(email) {
	const str = (email || '').trim();
	const position = str.lastIndexOf('@');

	if (position === -1) {
		return { local: str, domain: '' };
	}

	return { local: str.slice(0, position), domain: str.slice(position + 1) };
}

/** 'naver.com' → ['naver', 'com'] / 'yahoo.co.kr' → ['yahoo', 'co.kr'] */
function splitDomain(domain) {
	const position = domain.indexOf('.');

	if (position === -1) return [domain, ''];

	return [domain.slice(0, position), domain.slice(position + 1)];
}

/**
 * 두 문자열이 몇 글자나 다른지 센다.
 * 글자 두 개가 서로 뒤바뀐 경우(gmail → gmial)도 한 글자 차이로 센다. 가장 흔한 오타라서다.
 */
export function editDistance(a, b) {
	const rows = a.length;
	const cols = b.length;

	if (rows === 0) return cols;
	if (cols === 0) return rows;

	const table = Array.from({ length: rows + 1 }, () =>
		new Array(cols + 1).fill(0),
	);

	for (let i = 0; i <= rows; i++) table[i][0] = i;
	for (let j = 0; j <= cols; j++) table[0][j] = j;

	for (let i = 1; i <= rows; i++) {
		for (let j = 1; j <= cols; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;

			table[i][j] = Math.min(
				table[i][j - 1] + 1, // 삽입
				table[i - 1][j] + 1, // 삭제
				table[i - 1][j - 1] + cost, // 교체
			);

			// 앞뒤 두 글자가 서로 바뀐 경우
			if (
				i > 1 &&
				j > 1 &&
				a[i - 1] === b[j - 2] &&
				a[i - 2] === b[j - 1]
			) {
				table[i][j] = Math.min(table[i][j], table[i - 2][j - 2] + 1);
			}
		}
	}

	return table[rows][cols];
}

/**
 * 목록에 있는 도메인 중 오타로 보이는 것을 찾는다. 없으면 빈 문자열.
 *
 * 도메인을 '이름'과 '뒷부분'(첫 점 뒤 전체)으로 나눠 따로 비교한다.
 * naver.com 을 nate.net 으로 잘못 고쳐 주는 식의 엉뚱한 제안을 막으려는 것으로,
 * 이름은 한 글자까지만, 이름과 뒷부분을 합쳐서는 두 글자까지만 다를 때 제안한다.
 */
export function findSimilarDomain(domain, candidates = EMAIL_DOMAINS) {
	const [name, tld] = splitDomain(domain);

	let best = '';
	let bestDistance = Infinity;

	for (const candidate of candidates) {
		if (candidate === domain) return ''; // 오타가 아니다

		const [candidateName, candidateTld] = splitDomain(candidate);

		const nameDistance = editDistance(name, candidateName);
		if (nameDistance > 1) continue;

		const total = nameDistance + editDistance(tld, candidateTld);
		if (total === 0 || total > 2) continue;

		if (total < bestDistance) {
			best = candidate;
			bestDistance = total;
		}
	}

	return best;
}
